import { questions } from './questions'
import { types } from './types'
import { dimensionTraits } from './dimensions'
import { careerCategories } from './careers'

export const analysis = {
  calculateType(answers) {
    const dimensions = ['EI', 'SN', 'TF', 'JP']
    const scores = {}
    
    dimensions.forEach(dim => {
      scores[dim] = this.getDimensionStrength(answers, dim)
    })
    
    const type = dimensions.map(dim => scores[dim].dominant).join('')
    
    if (!types[type]) {
      console.error(`未找到类型: ${type}`)
      return {
        error: '无法确定准确的类型',
        type,
        scores
      }
    }
    
    return {
      type,
      scores,
      analysis: this.getPersonalityAnalysis(type, scores),
      reliability: this.calculateReliability(scores)
    }
  },

  getDimensionStrength(answers, dimension) {
    const dimensionQuestions = questions.filter(q => q.dimension === dimension)
    let totalScore = 0
    let totalWeight = 0

    dimensionQuestions.forEach(question => {
      const answer = answers[question.id - 1]
      const weight = question.weight || 1.0
      
      let baseScore = 0
      if (answer === 'strongA') baseScore = 2
      else if (answer === 'moderateA') baseScore = 1
      else if (answer === 'neutral') baseScore = 0
      else if (answer === 'moderateB') baseScore = -1
      else if (answer === 'strongB') baseScore = -2

      // 极性判断: valueA 是否对应维度首位字母
      const polarity = question.valueA === dimension[0] ? 1 : -1
      totalScore += baseScore * polarity * weight
      totalWeight += weight * 2
    })

    const preference = (totalScore + totalWeight) / (2 * totalWeight)
    
    return {
      preference,
      dimension,
      dominant: preference > 0.5 ? dimension[0] : dimension[1],
      strength: this.getPreferenceStrength(preference)
    }
  },

  getPreferenceStrength(preference) {
    const normalizedPreference = Math.abs(preference - 0.5) * 2
    if (normalizedPreference > 0.8) return '非常明显'
    if (normalizedPreference > 0.6) return '明显'
    if (normalizedPreference > 0.4) return '中等'
    if (normalizedPreference > 0.2) return '轻微'
    return '平衡'
  },

  calculateReliability(scores) {
    const preferences = Object.values(scores).map(s => Math.abs(s.preference - 0.5) * 2)
    const averageStrength = preferences.reduce((a, b) => a + b, 0) / preferences.length
    
    if (averageStrength > 0.7) return '高'
    if (averageStrength > 0.4) return '中'
    return '低'
  },

  getPersonalityAnalysis(type, scores) {
    if (!types[type]) {
      return {
        error: '无法获取性格分析',
        type,
        overview: '暂无数据',
        strengths: [],
        weaknesses: [],
        dimensionAnalysis: []
      }
    }

    return {
      overview: types[type].description,
      strengths: types[type].strengths,
      weaknesses: types[type].weaknesses,
      dimensionAnalysis: Object.entries(scores).map(([dim, score]) => ({
        dimension: dim,
        preference: score.preference,
        dominant: score.dominant,
        traits: dimensionTraits[score.dominant]?.traits || [],
        strength: score.strength,
        development: this.getDevelopmentSuggestions(dim, score)
      })),
      careerSuggestions: types[type].careers || [],
      developmentSuggestions: [
        ...types[type].weaknesses.map(w => `改进${w}的方面`),
        ...Object.values(scores)
          .filter(s => Math.abs(s.preference - 0.5) < 0.3)
          .map(s => `在${dimensionTraits[s.dominant]?.name || s.dominant}方面保持平衡`)
      ]
    }
  },

  getDevelopmentSuggestions(dimension, score) {
    const traits = dimensionTraits[score.dominant]
    if (!traits) {
      return {
        strengths: [],
        challenges: [],
        suggestions: ['暂无具体建议']
      }
    }
    
    return {
      strengths: traits.strengths || [],
      challenges: traits.challenges || [],
      suggestions: traits.suggestions || []
    }
  }
} 